// PullForm: scan or type a code, see what is in it, take some out.
//
// One form for every pull that starts from a code: repair (from a drone page),
// R&D and scrap (from a box), and later the scanner. The reason is fixed by the
// host through `mode`; the form never offers to change it.
//
// The code field is the home position. After a pull it clears and takes focus
// again, because the next thing to happen is another scan.

import React, { useEffect, useRef, useState } from 'react';
import Reveal from '../../components/ui/Reveal';
import { buttonClass } from '../../components/ui/Button';
import { itemLabel } from '../../components/ItemName';
import { Alert } from '../../components/ui/Alert';
import { Field } from '../../components/ui/Field';
import { RadioGroup, RadioRow } from '../../components/ui/Radio';
import {
  usePullsApi,
  type BoxItem,
  type PullInput,
  type PullReason,
  type PullResult,
  type Resolved,
} from './pullsApi';

export interface PullFormProps {
  mode: PullReason;
  /** Required for repair: the drone the part goes into. Shown, never edited. */
  serialnumber?: string;
  onPulled?: (result: PullResult) => void;
  className?: string;
}

const SUBMIT_LABEL: Record<PullReason, string> = {
  repair: 'Pull for repair',
  rnd: 'Pull for R&D',
  scrap: 'Pull to scrap',
};

let seq = 0;

const PullForm: React.FC<PullFormProps> = ({ mode, serialnumber, onPulled, className = '' }) => {
  const api = usePullsApi();
  const [uid] = useState(() => `pull-form-${++seq}`);
  const codeRef = useRef<HTMLInputElement>(null);
  const askRef = useRef(0);

  const [code, setCode] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [project, setProject] = useState('');
  const [projects, setProjects] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState<PullResult[]>([]);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  useEffect(() => {
    if (mode !== 'rnd') return undefined;
    let live = true;
    api
      .projects()
      .then((r) => {
        if (live) setProjects((r.projects || []).map((p) => p.project));
      })
      .catch(() => {
        // The list is a convenience; typing a project still works without it.
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const reset = () => {
    askRef.current += 1;
    setCode('');
    setResolved(null);
    setPickedId(null);
    setQuantity('1');
    setError(null);
    codeRef.current?.focus();
  };

  const resolveCode = async () => {
    const text = code.trim();
    if (!text || resolving) return;
    const ask = ++askRef.current;
    setResolving(true);
    setError(null);
    setResolved(null);
    setPickedId(null);
    try {
      const r = await api.resolve(text);
      if (ask !== askRef.current) return;
      setResolved(r);
      setQuantity('1');
      if (r.kind === 'box') {
        const open = r.items.filter((i) => i.pullable);
        if (r.items.length === 1 && open.length === 1) setPickedId(open[0].receipt_item_id);
      }
    } catch (err: any) {
      if (ask !== askRef.current) return;
      setError(err?.message || 'That code did not resolve. Check it and scan again.');
    } finally {
      if (ask === askRef.current) setResolving(false);
    }
  };

  const picked: BoxItem | null =
    resolved?.kind === 'box' ? resolved.items.find((i) => i.receipt_item_id === pickedId) ?? null : null;
  const isPart = !!resolved && resolved.kind !== 'box';
  const maxQty = picked ? picked.quantity_remaining : 1;
  const n = Number(quantity);
  const qtyOk = isPart || (Number.isInteger(n) && n >= 1 && n <= maxQty);
  const projectOk = mode !== 'rnd' || project.trim() !== '';
  const targetOk = resolved
    ? resolved.kind === 'box'
      ? !!picked
      : resolved.pullable
    : false;
  const canSubmit = targetOk && qtyOk && projectOk && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !resolved) return;
    const input: PullInput = { quantity: isPart ? 1 : n, reason: mode };
    if (resolved.kind === 'box') input.receipt_item_id = picked!.receipt_item_id;
    else if (resolved.kind === 'board') input.board_id = resolved.board_id;
    else input.part_instance_id = resolved.part_instance_id;
    if (mode === 'repair') input.serialnumber = serialnumber;
    if (mode === 'rnd') input.project = project.trim();
    setBusy(true);
    setError(null);
    try {
      const result = await api.pull(input);
      setSession((s) => [result, ...s]);
      onPulled?.(result);
      reset();
    } catch (err: any) {
      setError(err?.message || 'The pull did not go through. Nothing was taken. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {mode === 'repair' && serialnumber && (
        <p className="text-sm text-on-surface-variant">
          For drone <span className="font-mono text-on-surface">{serialnumber}</span>
        </p>
      )}

      <div>
        <label htmlFor={`${uid}-code`} className="mb-1 block text-xs text-on-surface-variant">
          Scan a box or part sticker, or type its code
        </label>
        <Field
          id={`${uid}-code`}
          ref={codeRef}
          data-drawer-autofocus
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              resolveCode();
            }
          }}
          className="m-0 font-mono"
        />
        <Reveal show={resolving}><p className="mt-1 text-xs text-on-surface-variant">Looking it up…</p></Reveal>
      </div>

      <div className="-mx-1 empty:hidden">
      <Reveal show={!!resolved}>
      <div className="px-1">
      {resolved && (
        <form onSubmit={submit} className="space-y-3">
          {resolved.kind === 'box' ? (
            <RadioGroup
              name={`${uid}-item`}
              label={resolved.items.length === 1 ? 'In this box' : 'Pick what to pull from this box'}
              value={pickedId}
              onChange={(id) => {
                setPickedId(id);
                setQuantity('1');
              }}
              className="space-y-1"
            >
              {resolved.items.map((it) => (
                <RadioRow
                  key={it.receipt_item_id}
                  value={it.receipt_item_id}
                  disabled={!it.pullable}
                  note={!it.pullable && it.refusal ? it.refusal : undefined}
                >
                  <span className="block text-sm">{itemLabel(it)}</span>
                  <span className="block text-xs tabular-nums text-on-surface-variant">
                    {it.quantity_remaining} in {it.location.name}
                  </span>
                </RadioRow>
              ))}
            </RadioGroup>
          ) : (
            <div
              className={`rounded-md px-3 py-2 ${
                resolved.pullable ? 'bg-surface-container' : 'bg-surface-container-low text-on-surface-variant'
              }`}
            >
              <span className="block text-sm text-on-surface">{itemLabel(resolved)}</span>
              {resolved.serialnumber && (
                <span className="block font-mono text-xs text-on-surface-variant">
                  {resolved.serialnumber}
                </span>
              )}
              {!resolved.pullable && resolved.refusal && (
                <span className="mt-0.5 block text-xs text-amber-300">{resolved.refusal}</span>
              )}
            </div>
          )}

          <Reveal show={!!picked}>
          {picked && (
            <label className="block">
              <span className="mb-1 block text-xs text-on-surface-variant">
                Quantity (up to {picked.quantity_remaining})
              </span>
              <Field
                type="number"
                inputMode="numeric"
                min={1}
                max={picked.quantity_remaining}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                aria-label="Quantity to pull"
                className="m-0 !w-32"
              />
            </label>
          )}
          </Reveal>

          {mode === 'rnd' && (
            <label className="block">
              <span className="mb-1 block text-xs text-on-surface-variant">Project</span>
              <Field
                type="text"
                list={`${uid}-projects`}
                autoComplete="off"
                value={project}
                onChange={(e) => setProject(e.target.value)}
                aria-label="Project"
                placeholder="Type a project, or pick a recent one"
                className="m-0"
              />
              <datalist id={`${uid}-projects`}>
                {projects.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={reset}
              className={buttonClass('quiet')}
            >
              Scan a different code
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={buttonClass('filled')}
            >
              {busy ? 'Pulling…' : SUBMIT_LABEL[mode]}
            </button>
          </div>
        </form>
      )}
      </div>
      </Reveal>
      </div>

      <Reveal show={!!error}><Alert>{error}</Alert></Reveal>

      <Reveal show={session.length > 0}>
        <section aria-label="Pulled this session" className="space-y-1">
          <h3 className="text-xs text-on-surface-variant">Pulled this session</h3>
          <ul className="space-y-1">
            {session.map((r) => (
              <li
                key={r.transaction_id}
                className="flex items-baseline justify-between gap-3 rounded-md bg-surface-container px-3 py-1.5 text-sm"
              >
                <span className="min-w-0 truncate text-on-surface">
                  <span className="tabular-nums">{r.quantity}</span> {itemLabel(r.item)}
                </span>
                <span className="shrink-0 text-xs text-on-surface-variant">from {r.from.name}</span>
              </li>
            ))}
          </ul>
        </section>
      </Reveal>
    </div>
  );
};

export default PullForm;
